import React, { useRef } from 'react'

import { HostServices } from '@ally/federated-types'
import { once } from '@ally/utilitarian'
import { Domains } from '@ally/data-types-host'
import { LoadState } from '@ally/use-data-domain'
import { Router as ReactRouter, Redirect } from 'react-router-dom'
import { muiBankTheme, MetronomeProvider, Box } from '@ally/metronome-ui'
import ErrorBoundary from '@ally/next-remote-error-boundary'
import Router from './routes'
import { HostServicesContext } from './HostServices'
import RouteSkeletonLoader from './components/RouteSkeletonLoader'

interface AppProps {
  services: HostServices
}

const App: React.FC<AppProps> = ({ services }) => {
  const { browserHistory, hostData } = services
  const name = 'ally-next-remote-beneficiaries'

  const accountsLoaded =
    hostData[Domains.AFG_ACCOUNTS].status.loadState === LoadState.LOADED
  const hostDataError =
    hostData[Domains.AFG_ACCOUNTS].status.loadState === LoadState.FAILED

  // allows for updating afg accounts without rendering skeleton loader
  const { isUpdating } = hostData[Domains.AFG_ACCOUNTS].status

  // Temporary fix to preserve path and query BEFORE the redirectors happen
  // This will eventually be replaced by a more permanent solution in Ally Next
  const { current: requireAuth } = useRef(
    once(() => {
      const redirect = encodeURIComponent(
        `${window.location.pathname}${window.location.search}`,
      )
      services.session.requireAuth(redirect)
    }),
  )
  requireAuth()

  const renderContent = (): React.ReactElement => {
    /* istanbul ignore next */
    if (hostDataError) {
      return <Redirect to="/error" />
    }
    if (!accountsLoaded && !isUpdating) {
      return <RouteSkeletonLoader />
    }
    return <Router />
  }

  return (
    <ReactRouter history={browserHistory}>
      <MetronomeProvider theme={muiBankTheme}>
        <HostServicesContext.Provider value={services}>
          <ErrorBoundary hostServices={services} name={name}>
            <Box id="beneficiaries-wrapper">{renderContent()}</Box>
          </ErrorBoundary>
        </HostServicesContext.Provider>
      </MetronomeProvider>
    </ReactRouter>
  )
}

export default App
